import React, { useEffect } from "react";
import { ApiInternal } from "src/api/api_internal";
import { UUID } from "src/api/api_common";
import { GetSingleUserResponse } from "src/api/generated/frontend_internal_v1";

/** Data provided by the {@link SINGLE_USER_CONTEXT} */
export type SingleUserContext = {
    /** The currently available user*/
    user: GetSingleUserResponse;

    /** Reload the users' information */
    reset: () => void;
};

/** {@link React.Context} to access the user */
const SINGLE_USER_CONTEXT = React.createContext<SingleUserContext>({
    user: {
        uuid: "",
        tenant: { uuid: "", name: "", description: "", badge_name: "", badge_color: "Blue" },
        created_at: "",
        display_name: "",
        groups: [],
        auth: "Remote",
        auth_specific: {
            type: "Remote",
        },
    },

    /**
     * Reset the user
     */
    reset: () => {},
});
SINGLE_USER_CONTEXT.displayName = "SingleUser";
export default SINGLE_USER_CONTEXT;

/**
 * The properties for {@link SingleUserProvider}
 */
export type SingleUserProviderProps = {
    /**
     * The specific user
     */
    uuid: UUID;

    /**
     * Children of the provider
     */
    children: React.ReactNode;
};

/**
 * The provider of a tags context
 */
export function SingleUserProvider(props: SingleUserProviderProps) {
    const [user, setUser] = React.useState<GetSingleUserResponse | "loading">("loading");
    let fetching = false;

    /**
     * Fetch the user
     */
    const fetchUser = async () => {
        if (fetching) return;
        fetching = true;

        const res = await ApiInternal.users.getSingle(props.uuid);
        setUser(res);

        fetching = false;
    };

    useEffect(() => {
        fetchUser().then();
    }, [props.uuid]);

    if (user === "loading") {
        return <div>Loading ..</div>;
    }

    return (
        <SINGLE_USER_CONTEXT.Provider
            value={{
                user: user,
                reset: fetchUser,
            }}
        >
            {props.children}
        </SINGLE_USER_CONTEXT.Provider>
    );
}
